import {Case} from '../services/api'
import {
    CASE_APPROVE_MODAL,
    CASE_REJECT_ATTORNEY_MODAL,
    CASE_REJECT_MODAL,
    CASE_RESUBMIT_MODAL
} from "../redux/reducers/modal";
import {history} from "../redux/store";

/**
 * The message the API answered with. axiosWrapper rejects with
 * `{code, data: {code, message, error_id}}` for anything the server refused, and with
 * `{message}` for a transport failure.
 */
const apiErrorMessage = (e: any, fallback: string): string =>
    (e && e.data && e.data.message) || (e && typeof e.message === 'string' && e.message) || fallback;

export const caseFormChange = (data: any) => {
    return (dispatch: any) => {
        return dispatch({type: 'CASE_FORM_ADD_CHANGE', ...data})
    }
};

export const priceFormChange = (data: any) => {
    return (dispatch: any) => {
        return dispatch({type: 'PRICE_FORM_ADD_CHANGE', ...data})
    }
};

export const resetPriceForm = () => {
    return (dispatch: any) => {
        return dispatch({type: 'RESET_PRICE_FORM'})
    }
};

export const resetCaseForm = () => {
    return (dispatch: any) => {
        return dispatch({type: 'RESET_CASE_FORM'})
    }
};

export const caseSubmit = (data: any) => {
    return async (dispatch: any) => {
        try {
            const payload = await Case.create(data);
            payload.created = true;
            return dispatch({type: 'CASE_SUBMIT', payload})
        } catch (e) {
            dispatch({type: 'CARD_API_ERROR', payload: e});
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    }
};

export const setCreatedStatus = (data: any) => {
    return async (dispatch: any) => {
        data.created = false;
    }
}

export const findLawyer = (data: any) => {
    return async (dispatch: any) => {
            return dispatch({type: 'FIND_LAWYER', payload: data})
    }
};

export const deleteCase = (case_uid: string) => {
    return async (dispatch: any) => {
        try {
            const payload = await Case.deleteCase(case_uid);
            return dispatch({type: 'SINGLE_CASE', payload})
        } catch (e) {
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    }
};

export const casesList = () => {
    return async (dispatch: any) => {
        try {
            const payload = await Case.list();
            return dispatch({type: 'CASES_LIST', payload})
        } catch (e) {
            return dispatch({type: 'CASES_LIST_ERROR', payload: e})
        }
    }
};

export const singleCase = (case_uid: string) => {
    return async (dispatch: any) => {
        try {
            const payload = await Case.singleCase(case_uid);
            return dispatch({type: 'SINGLE_CASE', payload})
        } catch (e) {
            return dispatch({type: 'CASES_LIST_ERROR', payload: e})
        }
    }
};

export const setSingleCase = (data: any) => {
    return async (dispatch: any) => {
        try {
            return dispatch({type: 'SINGLE_CASE', payload: data})
        } catch (e) {
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    }
};

export const caseAccept = (data: any) => {
    return (dispatch: any) => {
        return dispatch({type: 'CASE_ACCEPT', payload: data})
    }
};

export const caseReject = (data: any) => {
    return (dispatch: any) => {
        return dispatch({type: 'CASE_REJECT', payload: data})
    }
};

export const cancelCase = (data: any) => {
    return (dispatch: any) => {
        return dispatch({type: 'CASE_CANCEL', payload: data})
    }
};

export const acceptLawyer = (data: {
    lawyer_id: number,
    case_uid: string
}) => {
    return async (dispatch: any) => {
        try {
            await Case.acceptLawyer(data);
            const payload = await Case.singleCase(data.case_uid);
            dispatch({type: 'CASE_ACCEPT', payload: false});
            dispatch({type: 'CASE_REJECT', payload: false});
            return dispatch({type: 'SINGLE_CASE', payload})
        } catch (e) {
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    }
};

export const rejectLawyer = (data: {
    lawyer_id: number,
    case_uid: string
}) => {
    return async (dispatch: any) => {
        try {
            await Case.rejectLawyer(data);
            const payload = await Case.singleCase(data.case_uid);
            dispatch({type: 'CASE_ACCEPT', payload: false});
            dispatch({type: 'CASE_REJECT', payload: false});
            return dispatch({type: 'SINGLE_CASE', payload})
        } catch (e) {
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    };
};

export const approveCase = (data: string) => {
    return async (dispatch: any) => {
        try{
            const payload = await Case.approveCase({case_uid: data});
            dispatch({type: CASE_APPROVE_MODAL, payload: false});
            return dispatch({type: 'SINGLE_CASE', payload})
        } catch (e) {
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    }
};

export const declineCase = (data: string) => {
    return async (dispatch: any) => {
        try{
            const payload = await Case.rejectCase({case_uid: data});
            dispatch({type: CASE_REJECT_MODAL, payload: false});
            return dispatch({type: 'SINGLE_CASE', payload})
        } catch (e) {
            return dispatch({type: 'CASE_SUBMIT_ERROR', payload: e})
        }
    }
};

/**
 * Ask to unmatch the lawyer on a matched case. The server answers with the updated case
 * (status 20 - unmatch pending), which goes straight into the Case slice so the page
 * re-renders into the pending state.
 *
 * Resolves to `{success}` (plus `error` when refused) rather than the dispatch result: the
 * modal keeps the failure next to the field the consumer just typed into instead of
 * closing on an error nobody sees.
 */
export const rejectAttorney = (data: {
    case_uid: string,
    reason: string
}) => {
    return async (dispatch: any): Promise<{ success: boolean, error?: string }> => {
        try {
            const payload = await Case.rejectAttorney(data);
            dispatch({type: CASE_REJECT_ATTORNEY_MODAL, payload: false});
            dispatch({type: 'SINGLE_CASE', payload});
            return {success: true}
        } catch (e) {
            return {
                success: false,
                error: apiErrorMessage(e, 'We could not send your request. Please try again.')
            }
        }
    }
};

/**
 * Re-post a dead case. The server creates a NEW case with a NEW case_uid, so the consumer
 * cannot be left looking at the parent - we put the clone in the Case slice and move the
 * route to it.
 */
export const resubmitCase = (case_uid: string, data: {
    price?: number,
    comment?: string,
    date?: string,
    paymentMethod?: string
}) => {
    return async (dispatch: any): Promise<{ success: boolean, error?: string, case_uid?: string }> => {
        try {
            const payload = await Case.resubmit(case_uid, data);
            dispatch({type: CASE_RESUBMIT_MODAL, payload: false});
            dispatch({type: 'SINGLE_CASE', payload});
            if (payload && payload.case_uid && payload.case_uid !== case_uid) {
                history.push(`/case/${payload.case_uid}`);
            }
            return {success: true, case_uid: payload && payload.case_uid}
        } catch (e) {
            return {
                success: false,
                error: apiErrorMessage(e, 'We could not resubmit this case. Please try again.')
            }
        }
    }
};

export const updateCase = (case_uid: string, data: any) => {
    return async (dispatch: any) => {
        try{
            await Case.update(case_uid, data);
            const payload = await Case.singleCase(case_uid);

            dispatch({type: 'SINGLE_CASE', payload});
            return dispatch({type: 'EDIT_CASE', payload: false});
        } catch (e) {
            return dispatch({type: 'UPDATE_CASE_ERROR', payload: e})
        }
    }
};
